import React, { useEffect, useMemo, useState, Suspense, lazy, createElement } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FilterBar } from './components/FilterBar';
import { SchoolListings } from './components/SchoolListings';
import { ContentSection } from './components/ContentSection';
import { SeoContentSection } from './components/SeoContentSection';
import { Footer } from './components/Footer';
// Code-split route bundles — only the home page ships with the initial bundle.
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(m => ({
  default: m.ArticlesPage
})));
const CollegesPage = lazy(() => import('./pages/CollegesPage').then(m => ({
  default: m.CollegesPage
})));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage').then(m => ({
  default: m.ArticleDetailPage
})));
const ProgramLandingPage = lazy(() => import('./pages/ProgramLandingPage').then(m => ({
  default: m.ProgramLandingPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage').then(m => ({
  default: m.CookiePolicyPage
})));
const CANoticePage = lazy(() => import('./pages/CANoticePage').then(m => ({
  default: m.CANoticePage
})));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({
  default: m.PrivacyPolicyPage
})));
const RequestInformationPage = lazy(() => import('./pages/RequestInformationPage').then(m => ({
  default: m.RequestInformationPage
})));
const RequestInformationNewStartAssistancePage = lazy(() => import('./pages/RequestInformationNewStartAssistancePage').then(m => ({
  default: m.RequestInformationNewStartAssistancePage
})));
const ExploreBridgePage = lazy(() => import('./pages/ExploreBridgePage').then(m => ({
  default: m.ExploreBridgePage
})));
const RequestInformationV1Page = lazy(() => import('./pages/RequestInformationV1Page').then(m => ({
  default: m.RequestInformationV1Page
})));
const AreaOfStudyLandingPage = lazy(() => import('./pages/AreaOfStudyLandingPage').then(m => ({
  default: m.AreaOfStudyLandingPage
})));
const RequestInformationV3Page = lazy(() => import('./pages/RequestInformationV3Page').then(m => ({
  default: m.RequestInformationV3Page
})));
const FindDegreesBridgePage = lazy(() => import('./pages/FindDegreesBridgePage').then(m => ({
  default: m.FindDegreesBridgePage
})));
const FindDegreesDirectoryPage = lazy(() => import('./pages/FindDegreesDirectoryPage').then(m => ({
  default: m.FindDegreesDirectoryPage
})));
const ExploreDirectoryPage = lazy(() => import('./pages/ExploreDirectoryPage').then(m => ({
  default: m.ExploreDirectoryPage
})));
const AdvertiserTemplatePage = lazy(() => import('./pages/AdvertiserTemplatePage').then(m => ({
  default: m.AdvertiserTemplatePage
})));
const PartnerCollegesPage = lazy(() => import('./pages/PartnerCollegesPage').then(m => ({
  default: m.PartnerCollegesPage
})));
const CrestmontUniversityPage = lazy(() => import('./pages/CrestmontUniversityPage').then(m => ({
  default: m.CrestmontUniversityPage
})));
const CrestmontProgramPage = lazy(() => import('./pages/CrestmontProgramPage').then(m => ({
  default: m.CrestmontProgramPage
})));
import { useSchoolSearch } from './hooks/useSchoolSearch';
import { useGeoLocation } from './hooks/useGeoLocation';
import { useMetaTags } from './hooks/useMetaTags';
import { useMetaPixel } from './hooks/useMetaPixel';
import { useUrlParams } from './hooks/useUrlParams';
import { areaOfStudyData } from './data/areaOfStudyData';
import { useFacebookDomainVerification } from './hooks/useFacebookDomainVerification';
import { getDefaultProgram, getAreaFromProgram, isValidProgram } from './data/filterOptions';
function FilterAccordion({
  degreeLevel,
  selectedAreaId,
  selectedProgram,
  loading,
  schoolCount,
  onDegreeLevelChange,
  onAreaStudyChange,
  onProgramChange
}: {
  degreeLevel: number;
  selectedAreaId: number;
  selectedProgram: number;
  loading: boolean;
  schoolCount: number;
  onDegreeLevelChange: (v: number) => void;
  onAreaStudyChange: (v: number) => void;
  onProgramChange: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="w-full bg-white border-b border-gray-200 shadow-sm relative z-30" data-id="element-0" data-mp="zwiqktajbgw9">
      {/* Accordion toggle — works on both mobile and desktop */}
      <div className="max-w-7xl mx-auto px-4" data-id="element-1" data-mp="3knt0cx1gsnnc">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 md:py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2" data-id="element-2" data-mp="3koufpdpijwav">
          <div className="flex items-center gap-2.5" data-id="element-3" data-mp="1w2c4pf29ka6q">
            <SlidersHorizontalIcon className="w-4 h-4 text-[#3b82f6]" data-id="element-4" data-mp="2071zt1p3lkql" />
            <span className="text-sm font-bold text-gray-900" data-id="element-5" data-mp="255d6x4o0dh54">
              Find Your Program
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6" data-mp="29w3dgl867q1e">
              {loading ? '...' : `${schoolCount} result${schoolCount !== 1 ? 's' : ''}`}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7" data-mp="2js6gdtaujspe" />
        </button>
      </div>

      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-8" data-mp="2ssbm7z0e6j1g">
        <div className="px-4 pb-4 pt-1 max-w-7xl mx-auto" data-id="element-9" data-mp="yh6jqy11kcnp">
          <p className="text-xs text-gray-500 mb-3" data-id="element-10" data-mp="21r2dqaz8wvta">
            Select your degree of interest, area of interest, and program to see
            matching schools.
          </p>

          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={onDegreeLevelChange} onAreaStudyChange={onAreaStudyChange} onProgramChange={onProgramChange} data-id="element-11" data-mp="2as8ricaifsiu" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100" data-id="element-12" data-mp="2frd6jjk48kh3">
            <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-13" data-mp="2tii48g3xb2os">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-14" data-mp="2tkkyjcc3zhzb"></span>
              All programs are accredited
            </span>
            <span className="text-xs text-gray-400" data-id="element-15" data-mp="33g4rpqzag2kd">•</span>
            <span className="text-xs text-gray-500" data-id="element-16" data-mp="33elb1z9pzpi2">100% online options</span>
            <span className="text-xs text-gray-400 hidden sm:inline" data-id="element-17" data-mp="2yjo0gnwsl832">•</span>
            <span className="text-xs text-gray-500 hidden sm:inline" data-id="element-18" data-mp="3i4xymkqndwvs">
              Financial aid eligible
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export function App() {
  // Meta Business Manager domain verification tag.
  useFacebookDomainVerification();
  // Set favicon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.type = 'image/svg+xml';
    favicon.rel = 'icon';
    favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='0.9em' font-size='90'>🎓</text></svg>";
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  // Derive initial state from URL params or use defaults
  const initialProgram = urlParams.mProgram && isValidProgram(urlParams.mProgram) ? urlParams.mProgram : 900;
  const initialArea = urlParams.mProgram ? getAreaFromProgram(urlParams.mProgram) : 9;
  // Default the degree level filter to Bachelor's (value 3) when no
  // degreeInterest is supplied via URL params.
  const initialDegree = urlParams.degreeInterest ?? 3;
  // Derive currentPage from URL path
  const getPageFromPath = (pathname: string) => {
    const path = pathname.replace(/^\/+|\/+$/g, '');
    // Handle dynamic routes
    const pathMap: Record<string, string> = {
      'request-information': 'request-information',
      articles: 'articles',
      colleges: 'colleges',
      terms: 'terms',
      'privacy-policy': 'privacy-policy',
      'cookie-policy': 'cookie-policy',
      'ca-notice': 'ca-notice',
      'program-landing': 'program-landing',
      'partner-colleges': 'partner-colleges'
    };
    return pathMap[path] || 'compare';
  };
  const [currentPage, setCurrentPage] = useState<string>(getPageFromPath(location.pathname));
  const [currentArticle, setCurrentArticle] = useState<string | null>(null);
  const [degreeLevel, setDegreeLevel] = useState<number>(initialDegree);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(initialArea);
  const [selectedProgram, setSelectedProgram] = useState<number>(initialProgram);
  // Sync currentPage when location changes (e.g. Magic Patterns route dropdown)
  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);
  // Sync filter state when URL params change (e.g. arriving from /find-degrees/v6)
  useEffect(() => {
    if (urlParams.mProgram && isValidProgram(urlParams.mProgram)) {
      setSelectedProgram(urlParams.mProgram);
      setSelectedAreaId(getAreaFromProgram(urlParams.mProgram));
    }
    if (urlParams.degreeInterest != null) {
      setDegreeLevel(urlParams.degreeInterest);
    }
  }, [urlParams.mProgram, urlParams.degreeInterest]);
  // Build tracking params from URL (falls back to defaults inside the hook)
  const tracking = useMemo(() => ({
    sub1: urlParams.sub1 ?? undefined,
    sub2: urlParams.sub2 ?? undefined,
    sub3: urlParams.sub3 ?? undefined
  }), [urlParams]);
  const {
    geo
  } = useGeoLocation();
  useMetaPixel();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode,
    tracking
  });
  const currentAreaData = areaOfStudyData[selectedAreaId];
  const metaConfig = useMemo(() => {
    const configs: Record<string, {
      title: string;
      description: string;
      keywords: string;
    }> = {
      compare: {
        title: `Compare Online ${currentAreaData.name} Programs | DegreeCompare`,
        description: `Compare top accredited online ${currentAreaData.name.toLowerCase()} programs. Find the best online colleges, read reviews, and explore degree options for ${new Date().getFullYear()}.`,
        keywords: `online ${currentAreaData.name.toLowerCase()} degree, accredited online programs, compare online colleges, ${currentAreaData.name.toLowerCase()} programs, online education`
      },
      colleges: {
        title: 'Explore Accredited Online Colleges | DegreeCompare',
        description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
        keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory'
      },
      articles: {
        title: 'Articles & Guides for Online Students | DegreeCompare',
        description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
        keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice'
      }
    };
    return configs[currentPage] || configs.compare;
  }, [currentPage, currentAreaData]);
  useMetaTags({
    title: metaConfig.title,
    description: metaConfig.description,
    keywords: metaConfig.keywords,
    ogTitle: metaConfig.title,
    ogDescription: metaConfig.description
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  const handleProgramChange = (programId: number) => {
    // Derive the area from the program
    const newAreaId = getAreaFromProgram(programId);
    // If the area changes, update both area and program
    if (newAreaId !== selectedAreaId) {
      setSelectedAreaId(newAreaId);
    }
    setSelectedProgram(programId);
  };
  const handleNavigate = (page: string, articleSlug?: string) => {
    setCurrentPage(page);
    if (articleSlug) {
      setCurrentArticle(articleSlug);
    } else {
      setCurrentArticle(null);
    }
    // Update URL to match the current page
    let urlPath = '/';
    if (page === 'compare') {
      urlPath = '/';
    } else if (page === 'articles' && articleSlug) {
      // Article detail pages live at /articles/<slug>
      urlPath = `/articles/${articleSlug}`;
    } else if (page.startsWith('university/')) {
      urlPath = `/${page}`;
    } else {
      urlPath = `/${page}`;
    }
    navigate(urlPath);
    window.scrollTo(0, 0);
  };
  const handleNavigateToProgram = (areaId: number, programId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(programId);
    // Stay on compare page to show filtered results
    window.scrollTo(0, 0);
  };
  const handleNavigateToDegree = (degree: number) => {
    setDegreeLevel(degree);
    setSelectedAreaId(9);
    setSelectedProgram(900);
    setCurrentPage('compare');
    navigate('/');
    window.scrollTo(0, 0);
  };
  // Shared layout wrapper
  const isAdvertiserPage = location.pathname.startsWith('/universities/');
  // Explore bridge pages render their own nav + footer.
  const isBridgePage = location.pathname.startsWith('/find-degrees/') || location.pathname.startsWith('/explore/');
  // The v3 request-information template renders its own header + footer.
  const isSelfContainedTemplate = location.pathname.startsWith('/request-information/v3');
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => {
    if (isAdvertiserPage || isBridgePage || isSelfContainedTemplate) {
      return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col" data-id="element-19" data-mp="15ksdm8dytmwx">
          <main className="flex-grow" data-id="element-20" data-mp="15ifnh7y2068s">{children}</main>
        </div>;
    }
    return <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col" data-id="element-21" data-mp="1aduy7r0rlm0z">
        <Header currentPage={currentPage} onNavigate={handleNavigate} onNavigateToProgram={handleNavigateToProgram} onNavigateToDegree={handleNavigateToDegree} data-id="element-22" data-mp="1ecawir2aqgtp" />
        <main className="flex-grow" data-id="element-23" data-mp="1o8xcp2jogjpe">{children}</main>
        <Footer onNavigate={handleNavigate} data-id="element-24" data-mp="1yg8a9fosd7ix" />
      </div>;
  };
  const ComparePage = () => <>
      <HeroSection areaData={currentAreaData} selectedProgram={selectedProgram} degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} t1Override={urlParams.t1} data-id="element-25" data-mp="1tf2l1ruc42e5" />
      <FilterAccordion degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} loading={loading} schoolCount={schools.length} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={handleProgramChange} data-id="element-26" data-mp="1kh08qg55hnbk" />
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} onProgramChange={handleProgramChange} data-id="element-27" data-mp="23e9xmct8vt07" />
      <SeoContentSection areaStudyId={selectedAreaId} topSchool={schools[0]} data-id="element-28" data-mp="2v4emano1f278" />
      <ContentSection areaData={currentAreaData} selectedAreaId={selectedAreaId} onNavigate={handleNavigate} data-id="element-29" data-mp="t7dvh8qynfc6" />
    </>;
  return <PageLayout data-id="element-30" data-mp="y9364na9tqzq">
      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-id="element-32" data-mp="jh2rxnb6v85t">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" data-id="element-33" data-mp="ogxexfrufdll" />
          </div>} data-id="element-31" data-mp="ji411m8n9kkw">
        <Routes data-id="element-34" data-mp="agmm476z3oxs">
          <Route path="/" element={<ComparePage data-id="element-36" data-mp="fgit8fayvwhk" />} data-id="element-35" data-mp="ag4nj3wu8ymp" />
          <Route path="/request-information" element={<RequestInformationPage data-id="element-38" data-mp="j9nggkx2q6b" />} data-id="element-37" data-mp="fgslk9jhifqf" />
          <Route path="/request-information/v1" element={<RequestInformationV1Page data-id="element-40" data-mp="5ht9egzig1yo" />} data-id="element-39" data-mp="kjo9dj5mcgn" />
          <Route path="/request-information/v3" element={<RequestInformationV3Page data-id="element-42" data-mp="1uyca86ikzu5j" />} data-id="element-41" data-mp="5il6jw8ra7r2" />
          <Route path="/request-information/new-start-assistance" element={<RequestInformationNewStartAssistancePage data-id="element-44" data-mp="1zu9ma0mtaeci" />} data-id="element-43" data-mp="1zwdrclwg5jed" />
          {/* Explicit route so the first area landing shows up in the
              preview's route list; the dynamic route below serves every
              other <area-slug>/<version> combination. */}
          <Route path="/areas-of-study/psychology/v1" element={<AreaOfStudyLandingPage data-id="element-46" data-mp="3pd80cpznp5dp" />} data-id="element-45" data-mp="3u5snx9ryym8s" />
          <Route path="/areas-of-study/psychology/v2" element={<AreaOfStudyLandingPage data-id="element-48" data-mp="6xxczqec79z3" />} data-id="element-47" data-mp="3pbes569ead3l" />
          <Route path="/areas-of-study/health-care/v1" element={<AreaOfStudyLandingPage data-id="element-50" data-mp="309c86pyqmza" />} data-id="element-49" data-mp="2y6nrr4gl1wo" />
          <Route path="/areas-of-study/:areaSlug/:version" element={<AreaOfStudyLandingPage data-id="element-52" data-mp="by35f8sfl0i8" />} data-id="element-51" data-mp="gwmwtopsn4hz" />
          {/* Explicit route so this template shows up in the preview's
              route list; the dynamic route below serves every other
              {brand}-{version} slug. */}
          {/* Password-gated directory of every explore template. */}
          <Route path="/explore" element={<ExploreDirectoryPage data-id="element-54" data-mp="1rw98xgjt3uf2" />} data-id="element-53" data-mp="1rxkenlwtbc8t" />
          <Route path="/explore/UAGC-v1" element={<ExploreBridgePage data-id="element-56" data-mp="1wutzspd651qu" />} data-id="element-55" data-mp="1wvvo3vj73kqi" />
          <Route path="/explore/UAGC-v2" element={<ExploreBridgePage data-id="element-58" data-mp="21mvgcnhddemo" />} data-id="element-57" data-mp="1wu2ce1tq6abn" />
          <Route path="/explore/UMA-v1" element={<ExploreBridgePage data-id="element-60" data-mp="21oy93f9wplv3" />} data-id="element-59" data-mp="21nwtqyluyd7k" />
          <Route path="/explore/UMA-v2" element={<ExploreBridgePage data-id="element-62" data-mp="26l5cb0ax4j9c" />} data-id="element-61" data-mp="26lydzmv0sikm" />
          <Route path="/explore/UMA-v3" element={<ExploreBridgePage data-id="element-64" data-mp="18yzobw2qwaw8" />} data-id="element-63" data-mp="18xy3izvxga1b" />
          <Route path="/explore/search-v1" element={<ExploreBridgePage data-id="element-66" data-mp="1dwhoawysbfs3" />} data-id="element-65" data-mp="19011q778h9h4" />
          <Route path="/explore/:bridgeSlug" element={<ExploreBridgePage data-id="element-68" data-mp="1ivkuwluexu26" />} data-id="element-67" data-mp="1dxsvl6rfv5ny" />
          <Route path="/colleges" element={<CollegesPage data-id="element-70" data-mp="1nv7a30tsxkdm" />} data-id="element-69" data-mp="1iusy57p2xqab" />
          <Route path="/partner-colleges" element={<PartnerCollegesPage data-id="element-72" data-mp="1nwidgzjc4q47" />} data-id="element-71" data-mp="1nvgy4ivadhgo" />
          {/* Crestmont University runs its own brand-guide experience rather
              than the shared advertiser template. Listed before the generic
              :collegeSlug routes so it wins the match. */}
          <Route path="/universities/crestmont-university" element={<CrestmontUniversityPage data-id="element-74" data-mp="io6166yrm8dt" />} data-id="element-73" data-mp="nna8es24pw1i" />
          <Route path="/universities/crestmont-university/bba-programs" element={<CrestmontProgramPage slug="bba-programs" data-id="element-76" data-mp="dvwc0t5h5ccl" />} data-id="element-75" data-mp="iofqdsbzkbig" />
          <Route path="/universities/crestmont-university/aba-programs" element={<CrestmontProgramPage slug="aba-programs" data-id="element-78" data-mp="3zjaj93otu7a" />} data-id="element-77" data-mp="8ymdmo03xqcm" />
          <Route path="/universities/:collegeSlug" element={<AdvertiserTemplatePage data-id="element-80" data-mp="3w1955vwxdlg2" />} data-id="element-79" data-mp="3woqhiol6bee" />
          <Route path="/universities/:collegeSlug/:programSlug" element={<AdvertiserTemplatePage data-id="element-82" data-mp="3m4mkp88igwek" />} data-id="element-81" data-mp="3r3ha4t0zhj7n" />
          <Route path="/articles" element={<ArticlesPage onNavigate={handleNavigate} data-id="element-84" data-mp="1pc49ig61txhe" />} data-id="element-83" data-mp="3m5xx1wg0vcha" />
          <Route path="/articles/:slug" element={<ArticleDetailPage onNavigate={handleNavigate} onProgramChange={handleProgramChange} data-id="element-86" data-mp="1keks57e214l8" />} data-id="element-85" data-mp="1pa9ybtkkxjqi" />
          <Route path="/program-landing" element={<ProgramLandingPage programId={selectedProgram} degreeLevel={degreeLevel} onProgramChange={handleProgramChange} data-id="element-88" data-mp="22hj5dbdbhr5o" />} data-id="element-87" data-mp="277zlx4cfpsw9" />
          <Route path="/terms" element={<TermsPage data-id="element-90" data-mp="1thmfj6f6ym91" />} data-id="element-89" data-mp="1the13a3f78vq" />
          <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-92" data-mp="1oi1hqzbrcjxw" />} data-id="element-91" data-mp="1tertjagj4tdi" />
          <Route path="/ca-notice" element={<CANoticePage data-id="element-94" data-mp="1ojuki3j92k0m" />} data-id="element-93" data-mp="1ogyqurefqhhz" />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-96" data-mp="1jjggqxoolw8e" />} data-id="element-95" data-mp="1jif5as3r77q5" />
          {/* Directory of every bridge template; listed before the dynamic
              route so the bare path resolves to the index. */}
          <Route path="/find-degrees" element={<FindDegreesDirectoryPage data-id="element-98" data-mp="3dvwt8g82m6lp" />} data-id="element-97" data-mp="3dwy68q8nd362" />
          <Route path="/find-degrees/:templateId" element={<FindDegreesBridgePage data-id="element-100" data-mp="38vigwlzfsahv" />} data-id="element-99" data-mp="38xuzn1d0qovz" />
        </Routes>
      </Suspense>
    </PageLayout>;
}